/**
 * @fileoverview Phone Number Module Helper Utilities
 * 
 * This file contains utility functions for phone number formatting,
 * search criteria creation, and other common operations.
 */

import { DEFAULT_SEARCH_FILTERS, PhoneNumberContext } from '../types.js';

/**
 * Create initial search criteria from search filters
 * 
 * @param {SearchFilter[]} filters - Search filters configuration
 * @returns {SearchCriteria} Initial search criteria object
 */
export function createSearchCriteria(filters = []) {
  const criteria = {};

  filters.forEach(filter => {
    if (filter.enabled) {
      criteria[filter.type] = filter.defaultValue || '';
    }
  });

  return criteria;
}

/**
 * Format phone number for display
 * 
 * @param {string} phoneNumber - Phone number in E.164 format
 * @param {string} [format='friendly'] - Format type ('friendly', 'e164', 'national')
 * @returns {string} Formatted phone number
 */
export function formatPhoneNumber(phoneNumber, format = 'friendly') {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return phoneNumber || '';
  }

  // Remove all non-digit characters except leading +
  const cleaned = phoneNumber.replace(/[^\d+]/g, '');

  switch (format) {
    case 'e164':
      return cleaned.startsWith('+') ? cleaned : `+${cleaned}`;

    case 'national':
      if (cleaned.startsWith('+1') && cleaned.length === 12) {
        // US/Canada number
        const digits = cleaned.slice(2);
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      }
      return cleaned;

    case 'friendly':
    default:
      if (cleaned.startsWith('+1') && cleaned.length === 12) {
        // US/Canada number
        const digits = cleaned.slice(2);
        return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else if (cleaned.startsWith('+') && cleaned.length > 7) {
        // International number
        const countryCode = cleaned.slice(1, cleaned.length - 10);
        const number = cleaned.slice(-10);
        return `+${countryCode} ${number.slice(0, 3)} ${number.slice(3, 6)} ${number.slice(6)}`;
      }
      return cleaned;
  }
}

/**
 * Parse phone number to extract components
 * 
 * @param {string} phoneNumber - Phone number to parse
 * @returns {Object} Parsed phone number components
 */
export function parsePhoneNumber(phoneNumber) {
  const cleaned = phoneNumber.replace(/[^\d+]/g, '');
  
  if (cleaned.startsWith('+1') && cleaned.length === 12) {
    // US/Canada number
    const digits = cleaned.slice(2);
    return {
      countryCode: '1',
      areaCode: digits.slice(0, 3),
      exchange: digits.slice(3, 6),
      number: digits.slice(6),
      formatted: formatPhoneNumber(phoneNumber),
      e164: cleaned
    };
  } else if (cleaned.startsWith('+')) {
    // International number
    return {
      countryCode: cleaned.slice(1, cleaned.length - 10),
      number: cleaned.slice(-10),
      formatted: formatPhoneNumber(phoneNumber),
      e164: cleaned
    };
  }

  return {
    raw: phoneNumber,
    formatted: phoneNumber,
    e164: cleaned.startsWith('+') ? cleaned : `+${cleaned}`
  };
}

/**
 * Convert letters to numbers using phone keypad mapping
 * 
 * @param {string} letters - Letters to convert
 * @returns {string} Converted numbers
 */
export function convertLettersToNumbers(letters) {
  const keypadMap = {
    'A': '2', 'B': '2', 'C': '2',
    'D': '3', 'E': '3', 'F': '3',
    'G': '4', 'H': '4', 'I': '4',
    'J': '5', 'K': '5', 'L': '5',
    'M': '6', 'N': '6', 'O': '6',
    'P': '7', 'Q': '7', 'R': '7', 'S': '7',
    'T': '8', 'U': '8', 'V': '8',
    'W': '9', 'X': '9', 'Y': '9', 'Z': '9'
  };

  return letters.toUpperCase()
    .split('')
    .map(char => keypadMap[char] || char)
    .join('');
}

/**
 * Get default search filters for a context
 * 
 * @param {string} context - Module context
 * @returns {SearchFilter[]} Default search filters
 */
export function getDefaultSearchFilters(context) {
  return DEFAULT_SEARCH_FILTERS[context] || DEFAULT_SEARCH_FILTERS[PhoneNumberContext.DASHBOARD];
}

/**
 * Build search parameters for API request
 * 
 * @param {SearchCriteria} criteria - Search criteria
 * @param {Object} options - Additional options
 * @returns {URLSearchParams} URL search parameters
 */
export function buildSearchParams(criteria, options = {}) {
  const params = new URLSearchParams();

  // Add search criteria
  Object.entries(criteria).forEach(([key, value]) => {
    if (value && value.toString().trim()) {
      // Map frontend field names to backend parameter names
      const paramMap = {
        'area-code': 'areaCode',
        'postal-code': 'postalCode',
        'phone-exchange': 'phoneExchange',
        'letter-pattern': 'letterPattern',
        'number-pattern': 'numberPattern'
      };

      const paramName = paramMap[key] || key;
      params.append(paramName, value.toString().trim());
    }
  });

  // Add options
  if (options.limit) {
    params.append('limit', options.limit.toString());
  }

  if (options.offset) {
    params.append('offset', options.offset.toString());
  }

  return params;
}

/**
 * Filter search results based on criteria
 * 
 * @param {PhoneNumber[]} results - Search results to filter
 * @param {Object} filters - Additional client-side filters
 * @returns {PhoneNumber[]} Filtered results
 */
export function filterSearchResults(results, filters = {}) {
  let filtered = [...results];

  // Filter by capabilities
  if (filters.requireVoice) {
    filtered = filtered.filter(number => 
      number.capabilities && number.capabilities.voice
    );
  }

  if (filters.requireSMS) {
    filtered = filtered.filter(number => 
      number.capabilities && number.capabilities.sms
    );
  }

  // Filter by pricing
  if (filters.maxMonthlyFee) {
    filtered = filtered.filter(number => {
      if (!number.pricing || !number.pricing.monthly) return true;
      return number.pricing.monthly.isIncluded || 
             (number.pricing.monthly.amount / 100) <= filters.maxMonthlyFee;
    });
  }

  // Filter by location
  if (filters.preferredRegions && filters.preferredRegions.length > 0) {
    filtered = filtered.filter(number => 
      filters.preferredRegions.includes(number.region)
    );
  }

  return filtered;
}

/**
 * Sort search results
 * 
 * @param {PhoneNumber[]} results - Results to sort
 * @param {string} sortBy - Sort criteria ('price', 'location', 'number')
 * @param {string} sortOrder - Sort order ('asc', 'desc')
 * @returns {PhoneNumber[]} Sorted results
 */
export function sortSearchResults(results, sortBy = 'number', sortOrder = 'asc') {
  const sorted = [...results];

  sorted.sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'price':
        const priceA = a.pricing?.monthly?.amount || 0;
        const priceB = b.pricing?.monthly?.amount || 0;
        comparison = priceA - priceB;
        break;

      case 'location':
        const locationA = `${a.region || ''} ${a.locality || ''}`.trim();
        const locationB = `${b.region || ''} ${b.locality || ''}`.trim();
        comparison = locationA.localeCompare(locationB);
        break;

      case 'number':
      default:
        comparison = a.phoneNumber.localeCompare(b.phoneNumber);
        break;
    }

    return sortOrder === 'desc' ? -comparison : comparison;
  });

  return sorted;
}

/**
 * Calculate total cost for phone number purchase
 * 
 * @param {PhoneNumber} number - Phone number to calculate cost for
 * @param {Object} options - Calculation options
 * @returns {Object} Cost breakdown
 */
export function calculateTotalCost(number, options = {}) {
  if (!number.pricing) {
    return {
      setupFee: 0,
      monthlyFee: 0,
      total: 0,
      currency: 'USD',
      isIncluded: false
    };
  }

  const setupFee = number.pricing.setup?.amount || 0;
  const monthlyFee = number.pricing.monthly?.isIncluded ? 0 : (number.pricing.monthly?.amount || 0);
  const currency = number.pricing.currency || 'USD';

  // Calculate first month total
  let total = setupFee;
  if (!number.pricing.monthly?.isIncluded) {
    total += monthlyFee;
  }

  // Calculate for multiple months if specified
  if (options.months && options.months > 1) {
    total += monthlyFee * (options.months - 1);
  }

  return {
    setupFee: setupFee / 100, // Convert from cents to dollars
    monthlyFee: monthlyFee / 100,
    total: total / 100,
    currency,
    isIncluded: number.pricing.monthly?.isIncluded || false,
    breakdown: {
      setupFeeRaw: setupFee,
      monthlyFeeRaw: monthlyFee,
      totalRaw: total
    }
  };
}

/**
 * Generate search suggestions based on partial input
 * 
 * @param {string} input - Partial input
 * @param {string} type - Input type ('city', 'region', etc.)
 * @returns {string[]} Array of suggestions
 */
export function generateSearchSuggestions(input, type) {
  const suggestions = [];

  if (!input || input.length < 2) {
    return suggestions;
  }

  const inputLower = input.toLowerCase();

  switch (type) {
    case 'city':
      // Common US cities
      const cities = [
        'New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix',
        'Philadelphia', 'San Antonio', 'San Diego', 'Dallas', 'San Jose',
        'Austin', 'Jacksonville', 'Fort Worth', 'Columbus', 'Charlotte',
        'San Francisco', 'Indianapolis', 'Seattle', 'Denver', 'Washington',
        'Boston', 'El Paso', 'Nashville', 'Detroit', 'Oklahoma City',
        'Portland', 'Las Vegas', 'Memphis', 'Louisville', 'Baltimore'
      ];
      
      suggestions.push(...cities.filter(city => 
        city.toLowerCase().includes(inputLower)
      ).slice(0, 5));
      break;

    case 'area-code':
      // Common area codes
      const areaCodes = [
        '212', '213', '214', '215', '216', '217', '218', '219',
        '301', '302', '303', '304', '305', '307', '308', '309',
        '312', '313', '314', '315', '316', '317', '318', '319',
        '401', '402', '403', '404', '405', '406', '407', '408',
        '409', '410', '412', '413', '414', '415', '416', '417',
        '418', '419', '423', '424', '425', '430', '432', '434'
      ];
      
      suggestions.push(...areaCodes.filter(code => 
        code.includes(input)
      ).slice(0, 5));
      break;

    default:
      break;
  }

  return suggestions;
}

/**
 * Validate search input in real-time
 * 
 * @param {string} value - Input value
 * @param {string} type - Input type
 * @returns {Object} Validation result with suggestions
 */
export function validateSearchInput(value, type) {
  const result = {
    valid: true,
    error: null,
    suggestions: []
  };

  if (!value || !value.trim()) {
    return result;
  }

  switch (type) {
    case 'area-code':
      if (!/^\d{0,3}$/.test(value)) {
        result.valid = false;
        result.error = 'Area code must be 3 digits';
      }
      break;

    case 'postal-code':
      if (!/^\d{0,5}(-\d{0,4})?$/.test(value)) {
        result.valid = false;
        result.error = 'Invalid postal code format';
      }
      break;

    case 'phone-exchange':
      if (!/^\d{0,3}$/.test(value)) {
        result.valid = false;
        result.error = 'Phone exchange must be 3 digits';
      }
      break;

    case 'letter-pattern':
      if (!/^[A-Za-z]*$/.test(value)) {
        result.valid = false;
        result.error = 'Letter pattern must contain only letters';
      } else if (value.length > 10) {
        result.valid = false;
        result.error = 'Letter pattern cannot exceed 10 characters';
      }
      break;

    case 'number-pattern':
      if (!/^\d*$/.test(value)) {
        result.valid = false;
        result.error = 'Number pattern must contain only digits';
      } else if (value.length > 10) {
        result.valid = false;
        result.error = 'Number pattern cannot exceed 10 digits';
      }
      break;
  }

  // Generate suggestions if input is valid
  if (result.valid) {
    result.suggestions = generateSearchSuggestions(value, type);
  }

  return result;
}
